import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  AddCircleOutline as AddIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { FieldGroup, FieldSelectorDropdown } from './FieldSelectorDropdown';
import { AddFieldModal } from './AddFieldModal';

export interface ManualEntry {
  id?: string;
  fieldId: string;
  value: string;
}

// Stable per-row id for React keys (fieldId/value change as the user edits, so
// they can't be keys). Monotonic counter — unique within the session, no crypto.
let entrySeq = 0;
export const makeManualEntryId = (): string => `manual-entry-${++entrySeq}`;

interface ManualFieldEntryProps {
  fieldOptions: FieldGroup[];
  manualEntries: ManualEntry[];
  onManualEntriesChange: (entries: ManualEntry[]) => void;
  alreadyMappedFields: string[];
}

export const ManualFieldEntry: React.FC<ManualFieldEntryProps> = ({
  fieldOptions,
  manualEntries,
  onManualEntriesChange,
  alreadyMappedFields,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const requiredFields = useMemo(() => {
    const fields: string[] = [];
    fieldOptions.forEach((group) => {
      group.fields.forEach((field) => {
        if (field.required) fields.push(field.id);
      });
    });
    return fields;
  }, [fieldOptions]);

  const missingRequiredFields = useMemo(() => {
    return requiredFields.filter((fieldId) => !alreadyMappedFields.includes(fieldId));
  }, [requiredFields, alreadyMappedFields]);

  const handleAddEntry = () => {
    onManualEntriesChange([...manualEntries, { id: makeManualEntryId(), fieldId: '', value: '' }]);
  };

  const handleAutoAddMissing = () => {
    const newEntries = missingRequiredFields.map((fieldId) => ({ id: makeManualEntryId(), fieldId, value: '' }));
    onManualEntriesChange([...manualEntries, ...newEntries]);
  };

  const handleUpdateEntry = (index: number, updates: Partial<ManualEntry>) => {
    onManualEntriesChange(manualEntries.map((entry, i) => (i === index ? { ...entry, ...updates } : entry)));
  };

  const handleDeleteEntry = (index: number) => {
    onManualEntriesChange(manualEntries.filter((_, i) => i !== index));
  };

  // For each row, already-mapped = parent column mappings + other manual entries
  const getMappedForIndex = (index: number) => [
    ...alreadyMappedFields,
    ...manualEntries.filter((_, i) => i !== index).map((e) => e.fieldId).filter(Boolean),
  ];

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
      {/* Accordion header */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          cursor: 'pointer',
          bgcolor: 'grey.50',
          userSelect: 'none',
          '&:hover': { bgcolor: 'grey.100' },
          transition: 'background-color 0.15s ease',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
          Constant Field Values
          {manualEntries.length > 0 && (
            <Chip label={manualEntries.length} size="small" color="primary" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} />
          )}
        </Typography>
        <Tooltip
          title="Use this to set a fixed value for a field that applies to every row — useful when your file is missing a column entirely (e.g. all shipments share the same transport mode) or when you want to override a column with a constant."
          placement="top"
          arrow
        >
          <InfoIcon fontSize="small" sx={{ color: 'text.disabled' }} onClick={(e) => e.stopPropagation()} />
        </Tooltip>
        <Tooltip title="Add constant field" placement="top">
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => { e.stopPropagation(); setExpanded(true); setModalOpen(true); }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {expanded
          ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
      </Box>

      {/* Accordion body */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pt: 1.5, pb: 1.5, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
          {manualEntries.length > 0 ? (
            <>
              {/* Column headers — shown once */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 0.5, pl: '2px', pr: '36px' }}>
                <Typography variant="caption" sx={{ flex: 1, fontWeight: 700, color: 'text.primary' }}>Field</Typography>
                <Typography variant="caption" sx={{ flex: 1, fontWeight: 700, color: 'text.primary' }}>Value (all rows)</Typography>
              </Box>

              {/* Rows */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {manualEntries.map((entry, index) => (
                  <Box key={entry.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    {/* Field selector — same component as column headers */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <FieldSelectorDropdown
                        columnIndex={index}
                        columnName={`manual_${index}`}
                        options={fieldOptions}
                        currentMapping={entry.fieldId || undefined}
                        sampleData={[]}
                        onSelect={(fieldId) => handleUpdateEntry(index, { fieldId })}
                        alreadyMappedFields={getMappedForIndex(index)}
                      />
                    </Box>

                    {/* Value input */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {entry.fieldId?.includes('transport_mode') ? (
                        <Select
                          fullWidth
                          size="small"
                          value={entry.value}
                          onChange={(e) => handleUpdateEntry(index, { value: e.target.value })}
                          displayEmpty
                          sx={{
                            bgcolor: 'white',
                            '& .MuiSelect-select': { py: '5px !important', px: '8px !important', minHeight: '0 !important', lineHeight: '1.25rem', display: 'flex !important', alignItems: 'center', fontSize: '0.75rem' },
                          }}
                        >
                          <MenuItem value="" disabled>
                            <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }}>Select transport mode...</Typography>
                          </MenuItem>
                          <MenuItem value="Road">Road</MenuItem>
                          <MenuItem value="Rail">Rail</MenuItem>
                          <MenuItem value="Ferry">Ferry</MenuItem>
                          <MenuItem value="IWW">IWW (Inland Waterway)</MenuItem>
                          <MenuItem value="Sea">Sea</MenuItem>
                          <MenuItem value="Air">Air</MenuItem>
                        </Select>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          value={entry.value}
                          onChange={(e) => handleUpdateEntry(index, { value: e.target.value })}
                          placeholder="Enter value..."
                          disabled={!entry.fieldId}
                          sx={{
                            mb: 0,
                            bgcolor: 'white',
                            '& .MuiInputBase-input': { py: '5px !important', px: '8px !important', fontSize: '0.75rem', lineHeight: '1.25rem' },
                          }}
                        />
                      )}
                    </Box>

                    {/* Delete */}
                    <IconButton size="small" onClick={() => handleDeleteEntry(index)} color="error" sx={{ flexShrink: 0 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No constant values set. Click + in the header to add one.
            </Typography>
          )}
        </Box>
      </Collapse>

      <AddFieldModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAddManually={handleAddEntry}
        onAutoAddMissing={handleAutoAddMissing}
        missingRequiredCount={missingRequiredFields.length}
      />
    </Box>
  );
};
